/**
 * Router Utilities and Navigation Helpers
 *
 * Type-safe navigation helpers and route constants for TanStack Router.
 */

import { useNavigate, useRouterState } from '@tanstack/react-router';

/**
 * Valid locales supported by the application
 */
export const VALID_LOCALES = ['en', 'ru'] as const;
export type Locale = (typeof VALID_LOCALES)[number];

/**
 * Default locale for redirects
 */
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Type-safe route paths
 * Use these constants for navigation to ensure type safety
 */
export const ROUTES = {
  home: '/$locale',
  teamDashboard: '/$locale/team-dashboard',
  courses: {
    catalog: '/$locale/courses',
    detail: '/$locale/courses/$courseId',
  },
  instructor: {
    dashboard: '/$locale/instructor/dashboard',
    courses: '/$locale/instructor/courses',
    courseDetail: '/$locale/instructor/courses/$courseId',
    students: '/$locale/instructor/students',
  },
  student: {
    dashboard: '/$locale/student/my_courses',
    courseDetail: '/$locale/student/courses/$courseId',
  },
  admin: {
    dashboard: '/$locale/admin/overview',
    settings: '/$locale/admin/settings',
  },
  uiLibrary: '/$locale/ui-library',
  healthz: '/healthz',
} as const;

/**
 * Hook to get the current locale from the URL
 *
 * @returns The current locale or undefined if not in a locale route
 */
export function useLocale(): Locale | undefined {
  const routerState = useRouterState();
  const matches = routerState.matches;

  // Find the match with locale param
  for (const match of matches) {
    const params = match.params as Record<string, string>;
    if ('locale' in params && isValidLocale(params.locale)) {
      return params.locale;
    }
  }

  return undefined;
}

/**
 * Hook for locale-aware navigation
 *
 * @returns A navigation function that automatically includes the current locale
 */
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const locale = useLocale() ?? DEFAULT_LOCALE;

  return (path: string, options?: { replace?: boolean }) => {
    // Replace $locale placeholder with actual locale
    const localizedPath = path.replace('$locale', locale);
    void navigate({ to: localizedPath, replace: options?.replace });
  };
}

/**
 * Check if a string is a valid locale
 */
export function isValidLocale(locale: string): locale is Locale {
  return VALID_LOCALES.includes(locale as Locale);
}

/**
 * Get localized path by replacing $locale placeholder
 */
export function getLocalizedPath(path: string, locale: Locale): string {
  return path.replace('$locale', locale);
}

/**
 * Hook to get the current route section (public, instructor, student, admin)
 */
export function useRouteSection(): 'public' | 'instructor' | 'student' | 'admin' {
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  if (pathname.includes('/instructor')) return 'instructor';
  if (pathname.includes('/student')) return 'student';
  if (pathname.includes('/admin')) return 'admin';
  return 'public';
}
